import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, User, ArrowLeft, Share2, Loader2, Edit } from "lucide-react";
import { blogAPI } from "../services/api";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    loadBlogPost();
  }, [slug]);

  const loadBlogPost = async () => {
    try {
      const response = await blogAPI.getBlogById(slug);
      
      if (response.success) {
        setBlog(response.data);
        // Load related blogs
        loadRelatedBlogs(response.data);
      } else {
        toast.error(response.error.message);
      }
    } catch (error) {
      toast.error("Failed to load blog post");
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedBlogs = async (currentBlog) => {
    try {
      const response = await blogAPI.getBlogs({
        limit: 3,
        visibility: 'public'
      });
      
      if (response.success) {
        // Filter out current blog and get blogs with similar tags
        const related = response.data
          .filter(b => b.id !== currentBlog.id)
          .filter(b => b.tags?.some(tag => currentBlog.tags?.includes(tag)))
          .slice(0, 3);
        
        setRelatedBlogs(related);
      }
    } catch (error) {
      console.error("Failed to load related blogs:", error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
        toast.success("Shared successfully!");
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to clipboard
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleEdit = () => {
    // Navigate to edit page with blog ID in URL (edit routes still use ID)
    navigate(`/blog/edit/${blog.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-slate-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Blog Post Not Found</h1>
          <p className="text-slate-600 mb-6">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
            <Link to="/blog">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
            
            {isAuthenticated && (
              <Button onClick={handleEdit} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Edit className="w-4 h-4 mr-2" />
                Edit Post
              </Button>
            )}
          </div>

          {/* Title and Meta */}
          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
              {blog.title}
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              {blog.excerpt}
            </p>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500 mb-6">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                {blog.author}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {new Date(blog.publishedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {blog.readTime}
              </div>
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {blog.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Share Button */}
            <Button 
              onClick={handleShare}
              variant="outline" 
              className="mb-8"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Article
            </Button>
          </div>
        </div>
      </div>

      {/* Content with ReactMarkdown */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
            <div className="markdown-content prose prose-lg prose-slate max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  // Custom components for better styling
                  table: ({node, ...props}) => (
                    <div className="table-wrapper overflow-x-auto my-6">
                      <table className="min-w-full border-collapse border border-slate-300" {...props} />
                    </div>
                  ),
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-blue-500 pl-6 py-2 my-6 bg-blue-50 italic text-slate-700" {...props} />
                  ),
                  code: ({node, inline, className, children, ...props}) => {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline ? (
                      <div className="my-6">
                        <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto">
                          <code className={className} {...props}>
                            {children}
                          </code>
                        </pre>
                      </div>
                    ) : (
                      <code className="bg-slate-100 text-slate-800 px-2 py-1 rounded text-sm" {...props}>
                        {children}
                      </code>
                    );
                  },
                  h1: ({node, ...props}) => (
                    <h1 className="text-3xl font-bold text-slate-900 mt-8 mb-4 border-b border-slate-200 pb-2" {...props} />
                  ),
                  h2: ({node, ...props}) => (
                    <h2 className="text-2xl font-bold text-slate-900 mt-6 mb-3" {...props} />
                  ),
                  h3: ({node, ...props}) => (
                    <h3 className="text-xl font-semibold text-slate-900 mt-5 mb-2" {...props} />
                  ),
                  p: ({node, ...props}) => (
                    <p className="text-slate-700 leading-relaxed mb-4" {...props} />
                  ),
                  ul: ({node, ...props}) => (
                    <ul className="list-disc list-inside space-y-2 mb-4 text-slate-700" {...props} />
                  ),
                  ol: ({node, ...props}) => (
                    <ol className="list-decimal list-inside space-y-2 mb-4 text-slate-700" {...props} />
                  ),
                  li: ({node, ...props}) => (
                    <li className="text-slate-700" {...props} />
                  ),
                  a: ({node, ...props}) => (
                    <a className="text-blue-600 hover:text-blue-800 underline" {...props} />
                  ),
                  img: ({node, ...props}) => (
                    <img className="rounded-lg shadow-md my-6 max-w-full h-auto" {...props} />
                  ),
                  hr: ({node, ...props}) => (
                    <hr className="border-slate-300 my-8" {...props} />
                  )
                }}
              >
                {blog.content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedBlogs.map((relatedBlog) => (
                <Link
                  key={relatedBlog.id}
                  to={`/blog/${relatedBlog.slug}`}
                  className="group block bg-white border border-slate-200 rounded-lg p-6 hover:border-blue-200 hover:shadow-md transition-all duration-200"
                >
                  <div className="space-y-3">
                    {relatedBlog.tags && relatedBlog.tags.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {relatedBlog.tags[0]}
                      </Badge>
                    )}
                    <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {relatedBlog.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-3">
                      {relatedBlog.excerpt}
                    </p>
                    <div className="flex items-center text-xs text-slate-500">
                      <Calendar className="w-3 h-3 mr-1" />
                      {new Date(relatedBlog.publishedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage;
